/**
 * Hand-rolled, dependency-free validators for the two untyped boundaries in the
 * shell's module-loading path:
 *
 *   1. The gateway UI manifest (`GET /api/v1/ui/manifest`) — an over-the-wire
 *      JSON document whose shape the shell previously trusted with pervasive
 *      `any`. A malformed entry (missing `moduleId`, a `menus` that isn't an
 *      array, …) would slip through to the registries and surface later as an
 *      opaque render crash.
 *
 *   2. The loaded module bundle (a compiled-in local chunk or a federated
 *      remote). Module Federation can flatten the `{ default, views, … }` export
 *      in several shapes, so the loader probes a handful of candidate layouts.
 *      Parsing the candidates against an explicit {@link ModuleLoadedShape}
 *      discriminated union turns a shape mismatch into a precise, logged load
 *      error at the seam instead of a downstream "x.views is not a function".
 *
 * These are intentionally small structural validators — no zod / new dependency
 * (the shell deliberately stays dependency-light here). Each returns a typed
 * result the caller branches on; nothing throws on bad input.
 */

// ─── Manifest module entry ───────────────────────────────────────────────────

import type { Isa95Tier, ProductFamily } from '@tyndo/types';
import type { IsaGroup } from '../layout/isaGroups';

/** A menu descriptor as it arrives in the gateway manifest (loose by design). */
export interface ManifestMenu {
  menuId: string;
  label?: string;
  sequence?: number;
  icon?: string;
  parentMenuId?: string;
  [key: string]: unknown;
}

/** A view descriptor as it arrives in the gateway manifest. */
export interface ManifestView {
  viewId?: string;
  componentName?: string;
  viewType?: string;
  [key: string]: unknown;
}

/**
 * A single validated module entry from the gateway manifest. The array fields
 * are normalised to `[]` when absent so callers never branch on `undefined`.
 */
export interface ManifestModuleEntry {
  moduleId: string;
  name: string;
  federatedModuleName?: string;
  icon?: string;
  /**
   * ISA-95 categorization the gateway emits per module (from the canonical
   * taxonomy). Preserved by the `...raw` spread below and consumed by the
   * sidebar's `inferIsaGroup` so a module buckets from declared metadata
   * instead of a substring guess. All optional — an un-migrated module omits
   * them and keeps the substring fallback.
   */
  isa95Tier?: Isa95Tier;
  productFamily?: ProductFamily;
  isaGroup?: IsaGroup;
  menus: ManifestMenu[];
  views: ManifestView[];
  actions: unknown[];
  realtimeSubscriptions: unknown[];
  /** Any additional gateway-provided fields are preserved for the registry. */
  [key: string]: unknown;
}

export type ManifestParseResult =
  | { ok: true; module: ManifestModuleEntry }
  | { ok: false; reason: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Validates and normalises one module entry from the gateway manifest. A module
 * is rejected (with a reason the caller logs) only when it lacks the two fields
 * the shell cannot function without — `moduleId` and a display `name`; every
 * other field is normalised so a partially-populated v2 module entry still
 * registers its nav.
 */
export function parseManifestModule(raw: unknown): ManifestParseResult {
  if (!isRecord(raw)) {
    return { ok: false, reason: 'module entry is not an object' };
  }
  const moduleId = raw.moduleId;
  if (typeof moduleId !== 'string' || moduleId.trim() === '') {
    return { ok: false, reason: 'module entry has no string moduleId' };
  }
  // `name` falls back to moduleId so a manifest that omits the label still
  // renders a nav entry rather than being dropped.
  const name =
    typeof raw.name === 'string' && raw.name.trim() !== ''
      ? raw.name
      : moduleId;

  const federatedModuleName =
    typeof raw.federatedModuleName === 'string'
      ? raw.federatedModuleName
      : undefined;

  // Menus must each be an object carrying a string menuId; drop malformed ones
  // rather than poisoning the menu registry.
  const menus = asArray(raw.menus).filter(
    (m): m is ManifestMenu => isRecord(m) && typeof m.menuId === 'string',
  );
  const views = asArray(raw.views).filter((v): v is ManifestView =>
    isRecord(v),
  );

  return {
    ok: true,
    module: {
      ...raw,
      moduleId,
      name,
      federatedModuleName,
      icon: typeof raw.icon === 'string' ? raw.icon : undefined,
      // ISA-95 categorization the gateway may emit; narrowed to the known enum
      // values so a malformed/unknown value is dropped (→ substring fallback)
      // rather than mistyped through the `...raw` spread.
      isa95Tier: asIsa95Tier(raw.isa95Tier),
      productFamily: asProductFamily(raw.productFamily),
      isaGroup: asIsaGroup(raw.isaGroup),
      menus,
      views,
      actions: asArray(raw.actions),
      realtimeSubscriptions: asArray(raw.realtimeSubscriptions),
    },
  };
}

const ISA95_TIERS = new Set<Isa95Tier>([
  'level-0',
  'level-1',
  'level-2',
  'level-3',
  'level-4',
  'platform',
]);
const PRODUCT_FAMILIES = new Set<ProductFamily>([
  'plant-floor-edge',
  'mes-mom',
  'plm',
  'erp-business',
  'intelligence-sustainability',
  'platform-distribution',
]);
const ISA_GROUPS = new Set<IsaGroup>([
  'operate',
  'plan',
  'commerce',
  'people',
  'admin',
]);

function asIsa95Tier(value: unknown): Isa95Tier | undefined {
  return typeof value === 'string' && ISA95_TIERS.has(value as Isa95Tier)
    ? (value as Isa95Tier)
    : undefined;
}
function asProductFamily(value: unknown): ProductFamily | undefined {
  return typeof value === 'string' &&
    PRODUCT_FAMILIES.has(value as ProductFamily)
    ? (value as ProductFamily)
    : undefined;
}
function asIsaGroup(value: unknown): IsaGroup | undefined {
  return typeof value === 'string' && ISA_GROUPS.has(value as IsaGroup)
    ? (value as IsaGroup)
    : undefined;
}

/**
 * Locates the module-list array inside an over-the-wire manifest document,
 * tolerating the shapes a gateway might legitimately return:
 *
 *   { modules: [...] }   — the canonical `ManifestV2` envelope
 *   { data: [...] }      — a generic data-envelope wrapper
 *   { data: { modules } }— a data-envelope around the canonical shape
 *   [ ... ]              — a bare top-level array
 *
 * Returns the array plus a `shape` tag so the caller can warn when the shape was
 * anything other than the canonical `modules` key (an unexpected gateway shape
 * should be visible, not silently coerced — and must never crash the shell).
 */
function locateModuleList(raw: unknown): {
  list: unknown[];
  shape: 'modules' | 'data' | 'data.modules' | 'array' | 'none';
} {
  if (Array.isArray(raw)) return { list: raw, shape: 'array' };
  if (!isRecord(raw)) return { list: [], shape: 'none' };
  if (Array.isArray(raw.modules)) return { list: raw.modules, shape: 'modules' };
  if (Array.isArray(raw.data)) return { list: raw.data, shape: 'data' };
  if (isRecord(raw.data) && Array.isArray(raw.data.modules)) {
    return { list: raw.data.modules, shape: 'data.modules' };
  }
  return { list: [], shape: 'none' };
}

/**
 * Parses the module list of a gateway manifest response, returning the valid
 * entries plus a list of rejection reasons for the invalid ones (so the caller
 * can log them without aborting the whole load). `shapeWarning` is set when the
 * list was found somewhere other than the canonical `modules` key (or not at
 * all), so the caller can log an actionable warning instead of the shell
 * silently rendering an empty nav.
 */
export function parseManifestModules(raw: unknown): {
  modules: ManifestModuleEntry[];
  rejected: string[];
  shapeWarning?: string;
} {
  const { list, shape } = locateModuleList(raw);
  const modules: ManifestModuleEntry[] = [];
  const rejected: string[] = [];
  for (const entry of list) {
    const result = parseManifestModule(entry);
    if (result.ok) modules.push(result.module);
    else rejected.push(result.reason);
  }
  let shapeWarning: string | undefined;
  if (shape === 'none') {
    shapeWarning =
      'manifest had no recognisable module list (expected { modules: [...] }, { data: [...] }, or a top-level array)';
  } else if (shape !== 'modules') {
    shapeWarning = `manifest module list found under non-canonical shape '${shape}' (expected top-level 'modules')`;
  }
  return { modules, rejected, shapeWarning };
}

// ─── Default-view resolution ─────────────────────────────────────────────────

/**
 * Landing-surface name heuristic: a view whose id/component/label reads as the
 * module's dashboard/overview/home page. Matched against the LAST dot-segment
 * of the viewId (e.g. `inventory.view.dashboard` → `dashboard`) plus the
 * componentName, so `LimsDashboard` and `scada.view.overview` both hit.
 */
const LANDING_NAME_RE = /(dashboard|overview|home|landing)/i;

/**
 * Deterministic default-view index for a module's registration pass.
 *
 * Only ~a handful of modules declare an explicit default view, and the shell
 * previously stamped `isDefault: idx === 0` over whatever the module said —
 * so clicking a module landed on whichever view happened to be listed first.
 * This resolver makes the landing deterministic and sensible:
 *
 *   1. the module's own `isDefault: true` declaration (now honored);
 *   2. a view whose name reads as the module landing surface
 *      (dashboard / overview / home / landing);
 *   3. the view targeted by the module's FIRST menu entry (top-level menus in
 *      sequence order, then their children) resolved through its actionId;
 *   4. index 0 (the previous behaviour, as the final fallback).
 *
 * Pure and defensive: `menus`/`actions` arrive loosely typed from either the
 * gateway manifest or a federated bundle, so everything is narrowed before use.
 */
export function resolveDefaultViewIndex(
  views: ManifestView[],
  menus: ManifestMenu[],
  actions: unknown[],
): number {
  if (views.length <= 1) return 0;

  // 1 — module-declared default.
  const declared = views.findIndex((v) => v.isDefault === true);
  if (declared >= 0) return declared;

  // 2 — landing-surface name heuristic.
  const byName = views.findIndex((v) => {
    const viewId = typeof v.viewId === 'string' ? v.viewId : '';
    const tail = viewId.slice(viewId.lastIndexOf('.') + 1);
    const component = typeof v.componentName === 'string' ? v.componentName : '';
    return LANDING_NAME_RE.test(tail) || LANDING_NAME_RE.test(component);
  });
  if (byName >= 0) return byName;

  // 3 — first menu entry's view. Walk top-level menus in sequence order, then
  // each one's children (also in sequence order), resolving actionId → viewId.
  const viewIdByAction = new Map<string, string>();
  for (const a of actions) {
    if (!isRecord(a)) continue;
    const actionId = a.actionId;
    const viewId = a.viewId;
    if (typeof actionId === 'string' && typeof viewId === 'string') {
      viewIdByAction.set(actionId, viewId);
    }
  }
  const seq = (m: ManifestMenu): number =>
    typeof m.sequence === 'number' ? m.sequence : 0;
  const topLevel = menus
    .filter((m) => typeof m.parentMenuId !== 'string' || m.parentMenuId === '')
    .sort((a, b) => seq(a) - seq(b));
  const ordered: ManifestMenu[] = [];
  for (const root of topLevel) {
    ordered.push(root);
    ordered.push(
      ...menus
        .filter((m) => m.parentMenuId === root.menuId)
        .sort((a, b) => seq(a) - seq(b)),
    );
  }
  for (const menu of ordered) {
    const actionId = menu.actionId;
    if (typeof actionId !== 'string') continue;
    const targetViewId = viewIdByAction.get(actionId);
    if (!targetViewId) continue;
    const idx = views.findIndex((v) => v.viewId === targetViewId);
    if (idx >= 0) return idx;
  }

  // 4 — previous behaviour.
  return 0;
}

// ─── Loaded module bundle shape ──────────────────────────────────────────────

/**
 * The normalised view-loader map: componentName → async component import.
 * Federation exposes this as a named `views` export (an object), NOT the
 * `views: []` descriptor array that lives on the manifest, so the union below
 * distinguishes them.
 */
export type ViewLoaderMap = Record<string, () => Promise<unknown>>;
export type WidgetLoaderMap = Record<string, () => Promise<unknown>>;

/**
 * Discriminated result of validating a loaded module bundle. `kind` is the
 * discriminant: a `manifest` shape carries the descriptor arrays + the loader
 * maps the shell needs to register and lazily resolve components; an `empty`
 * shape loaded successfully but exposed nothing usable (surfaced as a degraded
 * remote); an `invalid` shape did not match any known federation layout.
 */
export type ModuleLoadedShape =
  | {
      kind: 'manifest';
      menus: ManifestMenu[];
      views: ManifestView[];
      actions: unknown[];
      widgets: unknown[];
      viewLoaders: ViewLoaderMap;
      widgetLoaders: WidgetLoaderMap;
    }
  | { kind: 'empty'; viewLoaders: ViewLoaderMap; widgetLoaders: WidgetLoaderMap }
  | { kind: 'invalid'; reason: string };

function pickLoaderMap(...candidates: unknown[]): ViewLoaderMap {
  for (const c of candidates) {
    // A loader map is an object (NOT an array — the array form is the manifest
    // descriptor list, not the lazy import map).
    if (isRecord(c) && !Array.isArray(c)) {
      return c as ViewLoaderMap;
    }
  }
  return {};
}

/**
 * Fold a supplementary loader map into the primary one WITHOUT disturbing the
 * primary's key order.
 *
 * Order matters because {@link resolveViewLoader}'s last-resort branch indexes
 * `Object.keys(viewLoaders)` positionally. Appending only the keys the primary
 * does not already have leaves every existing positional lookup byte-identical
 * while making the extra names resolvable by name — which is the whole point.
 */
function mergeLoaderMaps(primary: ViewLoaderMap, extra: ViewLoaderMap): ViewLoaderMap {
  const merged: ViewLoaderMap = { ...primary };
  for (const [name, loader] of Object.entries(extra)) {
    if (!(name in merged)) merged[name] = loader;
  }
  return merged;
}

/**
 * Validates a loaded module bundle (compiled-in chunk or federated remote) into
 * a {@link ModuleLoadedShape}. Mirrors the candidate-probing the loader did
 * inline, but returns a typed, branchable result instead of an `any` blob:
 *
 *  - finds the manifest object among the flattened export candidates (the one
 *    whose `menus` AND `views` are arrays),
 *  - resolves the lazy view/widget loader maps from the named exports,
 *  - reports `empty` when the bundle exposed no menus and no views,
 *  - reports `invalid` (with a reason) when the bundle is null/undefined.
 */
export function parseLoadedModule(loaded: unknown): ModuleLoadedShape {
  if (loaded == null || typeof loaded !== 'object') {
    return { kind: 'invalid', reason: 'loaded module is null or not an object' };
  }
  const l = loaded as Record<string, unknown>;
  const candidates = [
    l.default,
    l.moduleManifest,
    isRecord(l.default) ? (l.default as Record<string, unknown>).default : undefined,
    l,
  ];
  const manifest =
    candidates.find(
      (c): c is Record<string, unknown> =>
        isRecord(c) && Array.isArray(c.menus) && Array.isArray(c.views),
    ) ?? l;

  const menus = asArray(manifest.menus).filter(
    (m): m is ManifestMenu => isRecord(m) && typeof m.menuId === 'string',
  );
  const views = asArray(manifest.views).filter((v): v is ManifestView =>
    isRecord(v),
  );
  const actions = asArray(manifest.actions);
  const widgets = asArray(manifest.widgets);

  const defaultRec = isRecord(l.default)
    ? (l.default as Record<string, unknown>)
    : undefined;
  // A module may expose its lazy view imports under EITHER of two key styles,
  // and six first-party modules expose both:
  //
  //   `views`            — slug-keyed  (`change-management-workbench`)
  //   `componentLoaders` — componentName-keyed (`ChangeManagementWorkbench`)
  //
  // The descriptors carry `componentName`, so a slug-keyed map alone gives
  // resolveViewLoader nothing to match on and it falls through to its positional
  // last resort: `Object.keys(viewLoaders)[index]`. That silently binds a view to
  // whichever loader happens to sit at the same array index — which is how
  // `plm.view.changes` (descriptor 6) rendered the Document Management workbench
  // (slug-map key 6). It resolved a component, so nothing errored; it was simply
  // the wrong one, on every module whose two lists are not index-aligned.
  //
  // Folding the componentName map in makes those descriptors resolve BY NAME.
  // The merge appends only new keys, so the positional branch is unchanged for
  // the modules that were relying on it.
  const viewLoaders = mergeLoaderMaps(
    pickLoaderMap(l.views, defaultRec?.views),
    pickLoaderMap(l.componentLoaders, defaultRec?.componentLoaders),
  );
  const widgetLoaders = pickLoaderMap(l.widgets, defaultRec?.widgets);

  if (menus.length === 0 && views.length === 0) {
    return { kind: 'empty', viewLoaders, widgetLoaders };
  }

  return {
    kind: 'manifest',
    menus,
    views,
    actions,
    widgets,
    viewLoaders,
    widgetLoaders,
  };
}

/**
 * Resolves the lazy component import for a view from a {@link ViewLoaderMap},
 * applying the shell's fallback chain: exact componentName → the manifest's
 * descriptor at the same index (its real federated name) → the loader map's
 * key at the same index → its first key. Returns null when the bundle exports
 * no usable component, so the caller can reject with a precise error.
 */
export function resolveViewLoader(
  viewLoaders: ViewLoaderMap,
  componentName: string | undefined,
  descriptors: ManifestView[],
  index: number,
): (() => Promise<unknown>) | null {
  if (componentName && typeof viewLoaders[componentName] === 'function') {
    return viewLoaders[componentName]!;
  }
  const realName = (descriptors[index] ?? descriptors[0])?.componentName;
  if (realName && typeof viewLoaders[realName] === 'function') {
    return viewLoaders[realName]!;
  }
  const keys = Object.keys(viewLoaders);
  const fallbackKey = keys[index] ?? keys[0];
  if (fallbackKey && typeof viewLoaders[fallbackKey] === 'function') {
    return viewLoaders[fallbackKey]!;
  }
  return null;
}
